'use client';

import type { NextPage } from 'next';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import InputField from './components/inputField/inputField';
import styles from './get-notified1440px.module.scss';
import ButtonSimple from '../HeroBlock/components/ButtonSimple/ButtonSimple';
import { GetNotified1440pxType } from '@/types/getNotified.types';
import { useState, useRef } from 'react';
import SuccessModal from '../SuccessModal/SuccessModal';
import ErrorModal from '../ErrorModal/ErrorModal';
import { getAttributionPayload } from '@/utils/getAttributionPayload';

interface IFormInputs {
  name: string;
  email: string;
}

const GetNotified: NextPage<GetNotified1440pxType> = ({
  className = '',
  name: titleName,
  text,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<IFormInputs>({
    defaultValues: {
      name: '',
      email: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    try {
      const attribution = getAttributionPayload(formRef.current);
      const response = await fetch('/api/getNotified', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          ...attribution,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      reset();
      setIsSuccess(true);
    } catch (error) {
      setIsError(true);
      console.error('Error submitting form:', error);
    }
  };

  const filterCyrillicFromEmail = (value: string) => {
    return value.replace(/[а-яё]/gi, '');
  };

  return (
    <section className="container">
      <div className={[styles.getnotified1440px, className].join(' ')}>
        <section className={styles.container}>
          <div className={styles.leftcol}>
            <h2
              className={styles.title}
              dangerouslySetInnerHTML={{ __html: titleName }}
            ></h2>
            <div
              className={styles.sescriptor}
              dangerouslySetInnerHTML={{ __html: text }}
            ></div>
          </div>
          <form
            ref={formRef}
            className={styles.form}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <div className={styles.inputs}>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: 'Name is required',
                }}
                render={({ field, fieldState }) => (
                  <InputField
                    label="Your name"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Invalid email format',
                  },
                }}
                render={({ field, fieldState }) => (
                  <InputField
                    label="Your email"
                    value={field.value}
                    onChange={(value) =>
                      field.onChange(filterCyrillicFromEmail(value))
                    }
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>

            <ButtonSimple
              text={isSubmitting ? 'Subscribing...' : 'Subscribe'}
              designBlendingMode=""
              designBlendingMode1=""
              type="submit"
              disabled={isSubmitting}
            />
          </form>
        </section>
      </div>

      {isSuccess && <SuccessModal onClose={() => setIsSuccess(false)} />}
      {isError && <ErrorModal onClose={() => setIsError(false)} />}
    </section>
  );
};

export default GetNotified;
